import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import {
  AdminQueryKeys,
  createPlatformProvider,
  deletePlatformProvider,
  listPlatformProviders,
  testPlatformProvider,
  updatePlatformProvider,
  type CreatePlatformProviderRequest,
  type PlatformProviderResponse,
  type ProviderTestResponse,
  type UpdatePlatformProviderRequest,
} from '../api';

export function usePlatformProviders(): UseQueryResult<PlatformProviderResponse[]> {
  return useQuery({
    queryKey: AdminQueryKeys.providers(),
    queryFn: ({ signal }) => listPlatformProviders(signal),
    staleTime: 30_000,
  });
}

export function useCreatePlatformProvider() {
  const qc = useQueryClient();
  return useMutation<PlatformProviderResponse, Error, CreatePlatformProviderRequest>({
    mutationFn: (req) => createPlatformProvider(req),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: AdminQueryKeys.providers() });
    },
  });
}

export function useUpdatePlatformProvider() {
  const qc = useQueryClient();
  return useMutation<PlatformProviderResponse, Error, { id: string; request: UpdatePlatformProviderRequest }>({
    mutationFn: ({ id, request }) => updatePlatformProvider(id, request),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: AdminQueryKeys.providers() });
    },
  });
}

export function useDeletePlatformProvider() {
  const qc = useQueryClient();
  return useMutation<null, Error, string>({
    mutationFn: (id) => deletePlatformProvider(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: AdminQueryKeys.providers() });
    },
  });
}

export function useTestPlatformProvider() {
  return useMutation<ProviderTestResponse, Error, string>({
    mutationFn: (id) => testPlatformProvider(id),
  });
}
